import React, { useEffect, useState } from "react";
import { IconCircleCheck, IconAlertTriangle, IconInfoCircle, IconX } from "@tabler/icons";

// Toasts. The queue lives in this module, so a toast survives the page that
// raised it (e.g. "Saved" followed by a redirect). Any number of <ToastHost/>
// may be mounted (the app shell has a primary one and every <Page> renders
// one so pages also work outside the shell); exactly one of them renders:
// the primary host if mounted, otherwise the oldest one.

let items = [];
let toastSeq = 0;
let hostSeq = 0;
let hosts = []; // [{ id, primary }]
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

const dismiss = (id) => {
  items = items.filter((t) => t.id !== id);
  notify();
};

const pushToast = (kind, message, ms) => {
  if (message === null || message === undefined || message === "") return;
  const t = { id: ++toastSeq, kind, message: String(message) };
  items = [...items.slice(-3), t];
  notify();
  if (ms > 0) setTimeout(() => dismiss(t.id), ms);
};

export const toast = {
  success: (message, ms = 3500) => pushToast("success", message, ms),
  error: (message, ms = 6000) => pushToast("error", message, ms),
  info: (message, ms = 3500) => pushToast("info", message, ms),
  // Shows the API body's message with the right tone; returns true on success.
  fromApi: (body, fallback) => {
    const ok = Boolean(body) && Number(body.errorCode) === 0;
    pushToast(ok ? "success" : "error", (body && body.message) || fallback || (ok ? "Done" : "Something went wrong"), ok ? 3500 : 6000);
    return ok;
  },
  dismiss,
};

const activeHostId = () => {
  const primary = hosts.find((h) => h.primary);
  return (primary || hosts[0] || {}).id;
};

const ICONS = { success: IconCircleCheck, error: IconAlertTriangle, info: IconInfoCircle };

export function ToastHost({ primary = false }) {
  const [id] = useState(() => ++hostSeq);
  const [, setTick] = useState(0);
  useEffect(() => {
    const host = { id, primary };
    const rerender = () => setTick((n) => n + 1);
    hosts.push(host);
    listeners.add(rerender);
    notify();
    return () => {
      hosts = hosts.filter((h) => h !== host);
      listeners.delete(rerender);
      notify();
    };
  }, [id, primary]);

  if (activeHostId() !== id) return null;
  // The live region stays mounted (even when empty) so screen readers announce new toasts.
  return (
    <div className="pa-toasts" role="status" aria-live="polite">
      {items.map((t) => {
        const Icon = ICONS[t.kind] || IconInfoCircle;
        return (
          <div key={t.id} className={`pa-toast pa-toast-${t.kind}`}>
            <Icon size={18} stroke={2} className="pa-toast-icon" aria-hidden="true" />
            <span className="pa-toast-text">{t.message}</span>
            <button type="button" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
              <IconX size={16} stroke={2} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
